import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { CanComponentDeactivate } from 'app/guards';
import { HttpService, PaymentInfo } from 'app/services';
import { Observable, of, Subject } from 'rxjs';
import { switchMap, takeUntil, catchError, retry } from 'rxjs/operators';
import { locale as english } from './i18n/en';
import { Router } from '@angular/router';
import { AuthService } from 'app/services';
import { ModalService } from 'app/services';
import { environment } from 'environments/environment';
import { FormBuilder, Validators } from '@angular/forms';
import { states } from 'assets/states';
import { AddressObject } from '../../address-input/address-input.component';

@Component({
  selector: 'app-cb-payment-method',
  templateUrl: './cb-payment-method.component.html',
  styleUrls: ['./cb-payment-method.component.scss'],
})
export class CbPaymentMethodComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  changes: { remove?: string, add?: string } = {};
  private _unsubscribeAll: Subject<any>;
  recurly = (window as any).recurly;
  hostedPageUrl: SafeResourceUrl;
  showAdd: boolean = false;
  addBank: boolean = false;
  addCard: boolean = false;
  pmInfo: PaymentInfo;
  elements;

  cardPaymentInfoForm = this.fb.group({
    first_name: ['', [Validators.required, Validators.minLength(2)]],
    last_name: ['', [Validators.required, Validators.minLength(3)]],
    phone: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(11), Validators.pattern(/[0-9]{10,11}/)]],
    address1: ['', [Validators.required, Validators.minLength(2)]],
    address2: [''],
    city: ['', [Validators.required, Validators.minLength(4)]],
    state: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(2)]],
    postal_code: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(5), Validators.pattern(/[0-9]{5}/)]],
    country: ['US', [Validators.required, Validators.minLength(2), Validators.maxLength(2)]]
  });

  bankPaymentInfoForm = this.fb.group({
    routing_number: ['', [Validators.required, Validators.minLength(9)]],
    account_number: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(17)]],
    account_number_confirmation: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(17)]],
    account_type: ['checking', [Validators.required]],
    name_on_account: ['', [Validators.required, Validators.minLength(6)]],
    country: ['US', [Validators.required, Validators.minLength(2), Validators.maxLength(2)]]
  });

  get business() {
    return this.auth.currentBusiness;
  }

  get cardImg() {
    switch (this.pmInfo.cardType) {
      case 'American Express': return '/assets/images/logos/american.png';
      case 'MasterCard': return '/assets/images/logos/master.png';
      case 'Discover': return '/assets/images/logos/discover.png';
      case 'Visa': return '/assets/images/logos/visa.png';
    }
  }

  get cardState() {
    return this.elements?.elements[0]?.state;
  }

  get cardValid() {
    return this.cardState?.valid;
  }

  get errors(): string[] {
    let rtn = [];

    if (this.addCard || this.addBank) {
      for (let k of Object.keys(this.cardPaymentInfoForm.value)) {
        let control = this.cardPaymentInfoForm.controls[k];
        if ((control.valid || !control.touched) && (k !== 'state' || !control.value.state || !states.find(s => s.short === control.value.state.toUpperCase()))) continue;

        switch (k) {
          case 'first_name': rtn.push('First Name must be 2 or more characters'); break;
          case 'last_name': rtn.push('Last Name must be 3 or more characters'); break;
          case 'phone': rtn.push('Phone must be 10 or 11 characters'); break;
          case 'address1': rtn.push('Address must be 2 or more characters'); break;
          case 'city': rtn.push('City must be 3 or more characters'); break;
          case 'state': rtn.push('State invalid entry'); break;
          case 'postal_code': rtn.push('Zip Code must be 5 characters'); break;
          case 'country': rtn.push('Country invalid entry'); break;
        }
      }

      if (this.cardState) {
        if (this.cardState.number && !this.cardState.number.empty && !this.cardState.number.valid) rtn.push('Credit Card number invalid');
        if (this.cardState.expiry && !this.cardState.expiry.empty && !this.cardState.expiry.valid) rtn.push('Expiration Date invalid');
        if (this.cardState.cvv && !this.cardState.cvv.empty && !this.cardState.cvv.valid) rtn.push('CVV invalid');
      }
    }
    
    if (this.addBank) {
      for (let k of Object.keys(this.bankPaymentInfoForm.value)) {
        let control = this.bankPaymentInfoForm.controls[k];
        if ((control.valid || !control.touched) && (k !== 'account_number_confirmation' || !control.touched || this.bankPaymentInfoForm.controls['account_number'].value === control.value)) continue;

        switch (k) {
          case 'routing_number': rtn.push('Routing Number must be 9 digits'); break;
          case 'account_number': rtn.push('Account Number must be 3 to 17 digits'); break;
          case 'name_on_account': rtn.push('Name on Account must be 6 or more characters'); break;
          case 'country': rtn.push('Country invalid entry'); break;
          case 'account_number_confirmation': {
            if (control.value.length < 3 || control.value.length > 17) rtn.push('Account Number must be 3 to 17 digits');
            if (this.bankPaymentInfoForm.controls['account_number'].value !== control.value) rtn.push('Account Numbers do not match');
            break;
          }
        }
      }
    }

    return rtn;
  }

  constructor(
    private _fuseTranslationLoaderService: FuseTranslationLoaderService,
    private fb: FormBuilder,
    private http: HttpService,
    public sanitizer: DomSanitizer,
    private router: Router,
    private auth: AuthService,
    private modal: ModalService
  ) {
    this._fuseTranslationLoaderService.loadTranslations(english);
    this.recurly?.configure(environment.recurlyKey);
    this._unsubscribeAll = new Subject();
  }

  private getHostedPage(): void {
    this.http.chargebeePortalToken().pipe(
      catchError(e => {
        return this.modal.errorDialog(e);
      }),
      takeUntil(this._unsubscribeAll))
      .subscribe((res: any) => {
        this.hostedPageUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          res.url,
        );
      });
  }

  private async getPaymentMethod(init?: boolean) {
    let pm = await this.http.getPaymentInfo();
    if (pm) this.pmInfo = pm;
    else {
      this.showAdd = true;
      this.cardClick();
    }
  }

  ngOnInit(): void {
    if (this.business.isLegacy) this.getHostedPage();
    else this.getPaymentMethod();
  }

  ngOnDestroy(): void {
    if (!this.business.isLegacy) return this.reset();
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  canDeactivate(): boolean | Observable<boolean> {
    if (!this.auth.getToken()) window.location.pathname = "login";

    if (this.business.isLegacy) {
      return this.http.updatePaymentSource().pipe(
        catchError(e => {
          return this.modal.errorDialog(e);
        }),
        takeUntil(this._unsubscribeAll),
        switchMap(() => of(true)),
      );
    }

    return true;
  }

  onNumKeyDown(event) {
    if (!' Tab Backspace Delete ArrowLeft ArrowRight 1 2 3 4 5 6 7 8 9 0'.includes(` ${event.key}`)) {
      event.preventDefault();
      return false;
    }
  }

  addressSelected(address: AddressObject) {
    this.cardPaymentInfoForm.get('address1').setValue(address.street);
    this.cardPaymentInfoForm.get('city').setValue(address.city);
    this.cardPaymentInfoForm.get('state').setValue(address.state);
    this.cardPaymentInfoForm.get('postal_code').setValue(address.zip);
    this.cardPaymentInfoForm.get('country').setValue(address.country);
  }

  bankClick() {
    this.addBank = true;
    this.addCard = false;
    this.remCardElm();

    setTimeout(() => {
      this.elements = this.recurly?.Elements();
    }, 50);
  }

  cardClick() {
    this.addBank = false;
    this.addCard = true;

    setTimeout(() => {
      this.elements = this.recurly?.Elements();
      let card = this.elements.CardElement();
      card.attach('#recurly-payment-elements');
    }, 50);
  }

  remCardElm() {
    let relm = document.getElementById('recurly-payment-elements');
    if (relm.children?.length) relm.children[0].remove();
  }

  reset() {
    this.showAdd = false;
    this.addBank = false;
    this.addCard = false;
    this.elements = undefined;
    this.changes = {};

    this.cardPaymentInfoForm.reset();
    this.bankPaymentInfoForm.reset();

    //this.remCardElm();
    this.getPaymentMethod(true);
  }

  onDelete(event) {
    if (event.target.className === 'fa-solid fa-trash-can') {
      this.changes.remove = this.pmInfo.id;
      this.showAdd = true;

      if (this.pmInfo.cardType) this.cardClick();
      else this.bankClick();
    } else {
      this.reset();
    }
  }

  onSubmit(event) {
    const formElm: any = document.getElementById('recurly-payment-form');
    formElm.children[1].value = formElm.children[1].value.replace(/ |\(|\)|\-/g, '');
    event.preventDefault();

    const finish = async (err, token) => {
      if (err) {
        console.error(err);
        window.alert(err.message);
      } else {
        let dt = { ...this.cardPaymentInfoForm.value };
        dt.token = token.id;
        try {
        await this.http.updatePaymentMethod(token.id);
        } catch(err) {
          window.alert(err?.message);
        }
        this.reset();
      }
    }
    if (this.addBank) this.recurly.bankAccount.token(formElm, finish)
    else this.recurly?.token(this.elements, formElm, finish);
  }
}
